import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../assets/Maps.css';
import { ordersApi } from '../utils/api';
import { loadGoogleMaps } from '../utils/googleMaps';
import logoIcon from '../assets/logo/icon.svg';

export interface MapProps { }

interface OrderSummary {
    id: string;
    order_number?: string;
    customer_name?: string;
    customer_phone?: string;
    status?: string;
    updated_at?: string;
}

interface CustomerLocation {
    id: string;
    name: string;
    address: string;
    lat: number;
    lng: number;
    orderCount: number;
    orders: OrderSummary[];
    last_updated?: string;
}

const DEFAULT_CENTER = { lat: 25.3548, lng: 51.5326 };

const normalizeLocations = (payload: any[]): CustomerLocation[] => {
    if (!Array.isArray(payload)) {
        return [];
    }

    return payload
        .map((item) => {
            const lat = Number(item.lat ?? item.latitude);
            const lng = Number(item.lng ?? item.longitude);

            if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
                return null;
            }

            const orders: OrderSummary[] = Array.isArray(item.orders)
                ? item.orders.map((order: any) => ({
                    id: String(order.id ?? order.order_number ?? Math.random()),
                    order_number: order.order_number,
                    customer_name: order.customer_name,
                    customer_phone: order.customer_phone,
                    status: order.status,
                    updated_at: order.updated_at,
                }))
                : [];

            return {
                id: String(item.id ?? `${lat}:${lng}`),
                name: String(item.name ?? item.address ?? 'Delivery location'),
                address: String(item.address ?? item.name ?? 'Location shared via coordinates'),
                lat,
                lng,
                orderCount: Number(item.order_count ?? item.orderCount ?? orders.length ?? 0) || orders.length || 0,
                orders,
                last_updated: item.last_updated,
            } as CustomerLocation;
        })
        .filter(Boolean) as CustomerLocation[];
};

const formatDateTime = (timestamp?: string) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
};

export const Map: React.FC<MapProps> = () => {
    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<any>(null);
    const markersRef = useRef<any[]>([]);
    const infoWindowRef = useRef<any>(null);

    const [locations, setLocations] = useState<CustomerLocation[]>([]);
    const [selectedLocation, setSelectedLocation] = useState<CustomerLocation | null>(null);
    const [isLoadingLocations, setIsLoadingLocations] = useState(true);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [mapLoaded, setMapLoaded] = useState(false);
    const [mapError, setMapError] = useState<string | null>(null);

    useEffect(() => {
        let isActive = true;

        const fetchLocations = async () => {
            try {
                setFetchError(null);
                const data = await ordersApi.getLocations();
                if (!isActive) return;

                const normalized = normalizeLocations(data);
                setLocations(prevLocations => {
                    if (
                        prevLocations.length === normalized.length &&
                        prevLocations.every((prevLoc, index) => {
                            const nextLoc = normalized[index];
                            if (!nextLoc) return false;
                            const ordersMatch = prevLoc.orders.length === nextLoc.orders.length;
                            return (
                                prevLoc.id === nextLoc.id &&
                                prevLoc.orderCount === nextLoc.orderCount &&
                                prevLoc.lat === nextLoc.lat &&
                                prevLoc.lng === nextLoc.lng &&
                                prevLoc.last_updated === nextLoc.last_updated &&
                                ordersMatch
                            );
                        })
                    ) {
                        return prevLocations;
                    }

                    return normalized;
                });

                if (normalized.length === 0) {
                    setSelectedLocation(null);
                }
            } catch (error: any) {
                if (!isActive) return;
                const message = error?.message ?? 'Unable to load locations. Please try again later.';
                setFetchError(message);
            } finally {
                if (isActive) {
                    setIsLoadingLocations(false);
                }
            }
        };

        fetchLocations();
        const intervalId = window.setInterval(fetchLocations, 1000 * 60 * 2);

        return () => {
            isActive = false;
            window.clearInterval(intervalId);
        };
    }, []);

    useEffect(() => {
        return () => {
            markersRef.current.forEach(marker => marker.setMap(null));
            markersRef.current = [];
            mapInstanceRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (!selectedLocation && infoWindowRef.current) {
            infoWindowRef.current.close();
        }
    }, [selectedLocation]);

    useEffect(() => {
        if (!selectedLocation) {
            return;
        }

        const updatedLocation = locations.find(location => location.id === selectedLocation.id);

        if (!updatedLocation) {
            setSelectedLocation(null);
        } else if (updatedLocation !== selectedLocation) {
            setSelectedLocation(updatedLocation);
        }
    }, [locations, selectedLocation]);

    const initializeMap = useCallback(() => {
        if (!mapRef.current || !(window.google && window.google.maps)) {
            return;
        }

        if (!mapInstanceRef.current) {
            mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
                center: DEFAULT_CENTER,
                zoom: 12,
                styles: [
                    {
                        featureType: 'all',
                        elementType: 'geometry.fill',
                        stylers: [{ color: '#f5f5f5' }],
                    },
                    {
                        featureType: 'water',
                        elementType: 'geometry',
                        stylers: [{ color: '#c9c9c9' }],
                    },
                    {
                        featureType: 'road',
                        elementType: 'geometry',
                        stylers: [{ color: '#ffffff' }],
                    },
                ],
            });
        }

        markersRef.current.forEach(marker => marker.setMap(null));
        markersRef.current = [];

        if (!infoWindowRef.current) {
            infoWindowRef.current = new window.google.maps.InfoWindow();
        }

        if (!locations.length) {
            setMapLoaded(true);
            return;
        }

        const bounds = new window.google.maps.LatLngBounds();
        const markerIcon = {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: '#715bc0',
            fillOpacity: 0.9,
            strokeColor: '#ffffff',
            strokeWeight: 2,
        };

        locations.forEach(location => {
            const marker = new window.google.maps.Marker({
                position: { lat: location.lat, lng: location.lng },
                map: mapInstanceRef.current,
                title: `${location.name} (${location.orderCount} order${location.orderCount === 1 ? '' : 's'})`,
                icon: markerIcon,
            });

            const position = marker.getPosition();
            if (position) {
                bounds.extend(position);
            }

            marker.addListener('click', () => {
                setSelectedLocation(location);
                if (infoWindowRef.current) {
                    infoWindowRef.current.setContent(
                        `<div class="map-info-window">
                            <h3>${location.name}</h3>
                            <p>${location.address}</p>
                            <span class="order-count">${location.orderCount} order${location.orderCount === 1 ? '' : 's'}</span>
                        </div>`
                    );
                    infoWindowRef.current.open({
                        anchor: marker,
                        map: mapInstanceRef.current,
                        shouldFocus: false,
                    });
                }
            });

            markersRef.current.push(marker);
        });

        if (locations.length === 1) {
            mapInstanceRef.current.setCenter({ lat: locations[0].lat, lng: locations[0].lng });
            mapInstanceRef.current.setZoom(14);
        } else {
            mapInstanceRef.current.fitBounds(bounds, 56);
        }

        setMapLoaded(true);
    }, [locations]);

    useEffect(() => {
        if (isLoadingLocations) {
            return;
        }

        let isCancelled = false;
        setMapError(null);

        loadGoogleMaps()
            .then(() => {
                if (!isCancelled) {
                    initializeMap();
                }
            })
            .catch((error: Error) => {
                if (isCancelled) return;
                setMapError(error.message);
            });

        return () => {
            isCancelled = true;
        };
    }, [initializeMap, isLoadingLocations]);

    const totalOrders = useMemo(() => {
        return locations.reduce((sum, location) => sum + location.orderCount, 0);
    }, [locations]);


    return (
        <div className="page">
            <header className="page__head">
                <img src={logoIcon} alt="" className="page__logo" onError={(e) => e.currentTarget.style.display = 'none'} />
                <div className="page__titles">
                    <h1 className="page__title">Map</h1>
                    <p className="page__sub">
                        {isLoadingLocations
                            ? 'Loading delivery locations…'
                            : locations.length
                                ? `${totalOrders} active order${totalOrders === 1 ? '' : 's'} across ${locations.length} location${locations.length === 1 ? '' : 's'}`
                                : 'No delivery locations shared yet'}
                    </p>
                </div>
            </header>

            {fetchError && (
                <div className="banner banner--danger">{fetchError}</div>
            )}

            <div className="map-split">
                {mapError ? (
                    <div className="map-error">
                        <div className="error-content">
                            <h2>Map Unavailable</h2>
                            <p>{mapError}</p>
                            {locations.length > 0 && (
                                <div className="locations-list">
                                    <h3>Delivery Locations</h3>
                                    {locations.map(location => (
                                        <div key={location.id} className="location-item">
                                            <strong>{location.name}</strong>
                                            <span>{location.address}</span>
                                            <span className="order-count">{location.orderCount} order{location.orderCount === 1 ? '' : 's'}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="map-wrapper">
                        <div ref={mapRef} className="google-map" />
                        {(!mapLoaded || isLoadingLocations) && (
                            <div className="map-loading-overlay">
                                <span>{isLoadingLocations ? 'Loading locations…' : 'Preparing map…'}</span>
                            </div>
                        )}
                    </div>
                )}

                {selectedLocation && (
                    <div className="location-details">
                        <div className="location-card">
                            <button className="close-details" onClick={() => setSelectedLocation(null)}>
                                ×
                            </button>
                            <h3>{selectedLocation.name}</h3>
                            <p>{selectedLocation.address}</p>
                            <div className="location-stats">
                                <span className="order-badge">{selectedLocation.orderCount} order{selectedLocation.orderCount === 1 ? '' : 's'}</span>
                                {selectedLocation.last_updated && (
                                    <span className="last-updated">Updated {formatDateTime(selectedLocation.last_updated)}</span>
                                )}
                            </div>
                            {selectedLocation.orders.length > 0 && (
                                <div className="location-orders">
                                    <h4>Recent Orders</h4>
                                    <ul>
                                        {selectedLocation.orders.slice(0, 5).map(order => (
                                            <li key={order.id}>
                                                <strong>{order.order_number ?? 'Order'}</strong>
                                                {order.customer_name && <span> • {order.customer_name}</span>}
                                                {order.status && <span className="status"> ({order.status})</span>}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
